import * as gateway from 'sfx-web-contract';
import { DeepRequired } from '../../../types';
import { enumTransformer } from '../../../helpers/object-utils';
import { EProductTypes } from '../../../enums';
import { ForwardPriceRequestWithClient, IForwardQuote, IStreamData } from '../../../interfaces';
import { fromGatewayPoint, fromGatewayPrice } from '../stream-adapter-utils/stream-adapter-utils';
import { SymbolTypes } from '@fnx/fx-pad';

const productMap = new Map<SymbolTypes, gateway.ForwardProduct>([
    [ 'forward', gateway.ForwardProduct.FORWARDPRODUCT_FORWARD ],
    [ 'swap', gateway.ForwardProduct.FORWARDPRODUCT_SWAP ],
]);

const forwardTypeTransformer: (type: SymbolTypes) => gateway.ForwardProduct =
    enumTransformer('forward product type', productMap);

const productMapBack = new Map<SymbolTypes, EProductTypes>([
    [ 'forward', 'Forward' ],
    [ 'swap', 'FX Swap' ],
]);

const forwardTypeFromServerTransformer: (type: SymbolTypes) => EProductTypes =
    enumTransformer('forward be product type', productMapBack);

export function encodeForwardQuoteRequest(request: ForwardPriceRequestWithClient): Uint8Array {

    const fullRequest: DeepRequired<gateway.IForwardQuoteRequest> = {
        Type: forwardTypeTransformer(request.productType),
        ClientId: request.clientId,
        Quantity: request.quantity,
        Instrument: request.ccyPair,
        BaseCurrency: request.tradedCurrency,
        Tenor: request.date,
        FarTenor: request.productType === 'forward' ? undefined : request.dateFar,
        FarQuantity: request.productType === 'forward' ? undefined : request.farQuantity,
    };

    const quoteRequest = gateway.ForwardQuoteRequest.create(fullRequest);

    return gateway.ForwardQuoteRequest.encode(quoteRequest).finish();
}

export const decodeForwardQuoteResponse = (productType: SymbolTypes) =>
    ({ payload, communicationToken: requestId }: IStreamData<Uint8Array>): IForwardQuote => {
        const { Ask, Bid, QuoteID, Spread } = gateway.ForwardQuoteResponse.decode(payload);

        return {
            productType: forwardTypeFromServerTransformer(productType),
            requestId,
            quoteId: QuoteID,

            askPrice: fromGatewayPrice(Ask?.NearRate),
            bidPrice: fromGatewayPrice(Bid?.NearRate),

            askPointsNear: fromGatewayPoint(Ask?.NearPoints),
            bidPointsNear: fromGatewayPoint(Bid?.NearPoints),

            spotAskPrice: fromGatewayPrice(Ask?.SpotRate),
            spotBidPrice: fromGatewayPrice(Bid?.SpotRate),

            askPriceFar: fromGatewayPrice(Ask?.FarRate),
            bidPriceFar: fromGatewayPrice(Bid?.FarRate),

            askPointsFar: fromGatewayPoint(Ask?.FarPoints),
            bidPointsFar: fromGatewayPoint(Bid?.FarPoints),

            askSwapRate: fromGatewayPoint(Ask?.SwapRate),
            bidSwapRate: fromGatewayPoint(Bid?.SwapRate),

            spotSpread: fromGatewayPoint(Spread?.SpotRateDelta),
            forwardSpread: fromGatewayPoint(Spread?.ForwardRateDelta),
            swapSpread: fromGatewayPoint(Spread?.SwapRateDelta),

            swapMidRate: fromGatewayPrice(Spread?.SwapMidRate),
        };
    };
